// src/components/ContactSales.tsx

import React, { useState } from 'react';
import { isValidEmail, isValidPositiveNumber } from '../../utils/validations';
import ButtonWithLoading from '../common/ButtomWIthLoading';
import { zenworksServiceApi } from '../../service/zenworksService.api';
import { formatPhoneNumber, getUserInfo } from '../../utils/functions';
import {
  SALES_BOT_NAME,
  TAX1099_SALES_BOT_NAME,
  SALES_DESCRIPTION,
  TAX1099_SALES_DESCRIPTION,
  SALES_PRODUCT_NAME,
  TAX1099_SALES_PRODUCT_NAME,
  SALES_QUERY_TYPE,
  SALES_REASON,
  SALES_STATUS,
  SALES_SUBJECT,
  SALES_TYPE,
  SALES_ORIGIN,
  SALES_UI_TEXT,
  LOCAL_STORAGE_KEYS,
  TAX1099_SUPPORT_TYPE,
  TAX1099_SUBJECT,
} from '../../constants/constants';
import { postApi } from '../../service/ apiService';
import { SalesForceServiceApi } from '../../service/salesForce.api';
import socket from '../../socket';
import { ADD_LOADER_EVENT } from '../../redux/reducers/eventReducer';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../redux/store';
import PhoneInputField from '../common/PhoneInputField';

interface DynamicContactSalesProps {
  data: any;
  handleFormSubmit: (response: any, formFields?: any) => void;
}

interface ClientConfig {
  lastUpdatedBy: string;
  productName: string;
  noOfFormsLabel: string;
  placeholderValue: string;
  checkUserApi: (reqbody: any) => Promise<any>;
  getDescriptionFn: (numberofforms: string) => string;
}

interface FormData {
  email: string;
  name: string;
  formCount: string;
  phone: string;
}

interface ApiResults {
  userCheckResult?: any;
  caseTicket?: string;
  leadTicket?: string;
}

const clientType = process.env.REACT_APP_CLIENT as string;
// const formcount1 = process.env.REACT_APP_FORMCOUNT1 as string;
// const formcount2 = process.env.REACT_APP_FORMCOUNT2 as string;

// Configuration for different client types using switch case
const getClientConfig = (): ClientConfig => {
  switch (clientType) {
    case 'salesbot2290ai':
      return {
        lastUpdatedBy: SALES_BOT_NAME,
        productName: SALES_PRODUCT_NAME,
        noOfFormsLabel: SALES_UI_TEXT.FORMS_LABEL,
        placeholderValue: SALES_UI_TEXT.FORMS_PLACEHOLDER,
        checkUserApi: zenworksServiceApi.checkUser,
        getDescriptionFn: SALES_DESCRIPTION,
      };

    case 'salesbottax1099':
      return {
        lastUpdatedBy: TAX1099_SALES_BOT_NAME,
        productName: TAX1099_SALES_PRODUCT_NAME,
        noOfFormsLabel: SALES_UI_TEXT.TAX1099_FORMS_LABEL,
        placeholderValue: SALES_UI_TEXT.TAX1099_FORMS_PLACEHOLDER,
        checkUserApi: zenworksServiceApi.checkExist,
        getDescriptionFn: TAX1099_SALES_DESCRIPTION,
      };

    default:
      return {
        lastUpdatedBy: SALES_BOT_NAME,
        productName: SALES_PRODUCT_NAME,
        noOfFormsLabel: SALES_UI_TEXT.FORMS_LABEL,
        placeholderValue: SALES_UI_TEXT.FORMS_PLACEHOLDER,
        checkUserApi: zenworksServiceApi.checkUser,
        getDescriptionFn: SALES_DESCRIPTION,
      };
  }
};

const sendUserProfile = async (
  userNumber: string | null,
  email: string,
  formCount: string,
  phone: string,
  lastUpdatedBy: string,
  ticket?: string,
): Promise<void> => {
  if (!userNumber) return;

  const userInfo: any = {
    user_id: userNumber,
    email: email,
    additional_info: { formCount},
    phone: phone,
    last_updated_by: lastUpdatedBy,
    browser: getUserInfo(),
  };

  if (ticket) {
    userInfo.salesforce_case_id = ticket;
  }

  await postApi(userInfo, `api/client/zenworks/v1/users/${userNumber}`);
};

const createSalesCase = async (
  email: string,
  numberofforms: string,
  config: ClientConfig,
): Promise<string | undefined> => {
  try {
    const response = await zenworksServiceApi.createCase({
      SuppliedName: '',
      SuppliedEmail: email,
      Product: config.productName,
      Status: SALES_STATUS,
      Origin: SALES_ORIGIN,
      Type: SALES_TYPE,
      Subject: SALES_SUBJECT(numberofforms),
      Reason: SALES_REASON,
      QueryType: SALES_QUERY_TYPE,
      Description: config.getDescriptionFn(numberofforms),
    });
    return response?.content?.referenceID;
  } catch (error) {
    console.error('Error creating case:', error);
    throw error;
  }
};

const createSupportCase = async (
  email: string,
  numberofforms: string,
  config: ClientConfig,
): Promise<string | undefined> => {
  try {
    const response = await zenworksServiceApi.createCase({
      SuppliedName: '',
      SuppliedEmail: email,
      Product: config.productName,
      Status: SALES_STATUS,
      Origin: SALES_ORIGIN,
      Type: TAX1099_SUPPORT_TYPE,
      Subject: TAX1099_SUBJECT,
      Reason: SALES_REASON,
      QueryType: '',
      Description: config.getDescriptionFn(numberofforms),
    });
    return response?.content?.referenceID;
  } catch (error) {
    console.error('Error creating case:', error);
    throw error;
  }
};

const createLead = async (
  email: string,
  name: string,
  numberofforms: string,
  phone: string,
): Promise<string | undefined> => {
  try {
    const response = await SalesForceServiceApi.createLead({
      product: 'tax1099',
      leadtype: 'AI Salesbot',
      companyname: 'zenwork',
      firstname: null,
      lastname: null,
      email: email,
      contactnumber: phone,
      numberofforms,
      interestedinapi: 'No',
      source: 'Organic',
      apilist: null,
      message: null,
      fullname: name,
      tinmatchvolume: null,
      tinmatchtype: null,
      w9Requestor1099filing: null,
      businesstype: null,
      accountingsoftware: null,
    });
    return response?.content?.referenceID;
  } catch (error) {
    console.error('Error creating lead:', error);
    throw error;
  }
};

const determineFormCountCategory = (formCount: number): string => {
  if (formCount > 10000) return '10001+';
  if (formCount > 1000) return '1001-10000';
  return formCount.toString();
};

// const getNextActionType = (
//   clientType: string,
//   userCheckResult: any,
//   data: any
// ): string => {
//   switch (clientType) {
//     case 'salesbot2290ai': {
//       // For salesbot2290ai, check if user exists to determine action
//       const userExists = userCheckResult?.success && userCheckResult?.content?.isExists;
//       return userExists ? data.data[1].actionType : data.data[0].actionType;
//     }

//     case 'salesbottax1099': {
//       // For salesbottax1099, check if user exists to determine action (same logic as salesbot2290ai)
//       const userExists = userCheckResult?.success && userCheckResult?.message === "User Exists";
//       return userExists ? data.data[0].actionType : data.data[1].actionType;
//     }
//     default:
//       return data.data[0].actionType;
//   }
// };

const shouldCreateCaseOrLead = (clientType: string, formCount: number): {
  shouldCreateSupportCase: boolean;
  shouldCreateSalesCase: boolean;
  shouldCreateLead: boolean;
} => {
  switch (clientType) {
    case 'salesbot2290ai':
      return {
        shouldCreateSupportCase: false,
        shouldCreateSalesCase: formCount > 1,
        shouldCreateLead: false,
      };

    case 'salesbottax1099':
      return {
        shouldCreateSupportCase: formCount > 0 && formCount <= 1000,
        shouldCreateSalesCase: formCount > 0 && formCount <= 1000,
        shouldCreateLead: formCount > 1000,
      };

    // Add more client types here as they are introduced
    // case 'salesbot1040':
    //   return {
    //     shouldCreateCase: formCount > 5,
    //     shouldCreateLead: formCount > 500,
    //   };

    default:
      return {
        shouldCreateSupportCase: false,
        shouldCreateSalesCase: false,
        shouldCreateLead: false,
      };
  }
};

const processApiCalls = async (
  form: FormData,
  config: ClientConfig,
): Promise<ApiResults> => {
  const formCount = parseInt(form.formCount) || 0;
  const userNumber = localStorage.getItem(LOCAL_STORAGE_KEYS.userNumber);
  const reqbody = { email: form.email, captcha: 'test' };

  // Start user check API immediately (this is the critical path)
  const userCheckPromise = config.checkUserApi(reqbody);

  const userCheckAns = await userCheckPromise.catch(error => {
    console.error('User check API failed:', error);
    return null;
  });

  let shouldCreateSupportCase = false;
  let shouldCreateSalesCase = false;
  let shouldCreateLead = false;

  // Identify client type
  const clientType = config.productName?.toLowerCase().includes('tax1099')
    ? 'salesbottax1099'
    : 'salesbot2290ai';

  if (clientType === 'salesbottax1099' || clientType === 'salesbot2290ai') {
    const userExists = userCheckAns?.content?.isExists;
    console.log(`user exists--- ${userExists}`);

    if (userExists) {
      // Existing user → always create a case
      shouldCreateSupportCase = true;
      shouldCreateLead = false;
    } else {
      // New user logic
      shouldCreateSalesCase = true;
      shouldCreateLead = formCount >= 1000;
    }
  } else {
    // Default behavior for other clients
    const { shouldCreateSalesCase: scCase, shouldCreateLead: scLead } =
      shouldCreateCaseOrLead(clientType, formCount);
    shouldCreateSalesCase = scCase;
    shouldCreateLead = scLead;
  }

  // Prepare API promises array
  const apiPromises: Promise<any>[] = [userCheckPromise];

  let caseSupportPromise: Promise<string | undefined> | null = null;
  if (shouldCreateSupportCase) {
    caseSupportPromise = createSupportCase(form.email, formCount.toString(), config);
    apiPromises.push(caseSupportPromise);
  }

  // Add case creation promise if needed
  let caseSalesPromise: Promise<string | undefined> | null = null;
  if (shouldCreateSalesCase) {
    caseSalesPromise = createSalesCase(form.email, formCount.toString(), config);
    apiPromises.push(caseSalesPromise);
  }

  // Add lead creation promise if needed
  let leadPromise: Promise<string | undefined> | null = null;
  if (shouldCreateLead) {
    const formCountCategory = determineFormCountCategory(formCount);
    leadPromise = createLead(form.email, form.name, formCountCategory, form.phone);
    apiPromises.push(leadPromise);
  }

  // Wait for all API calls to complete
  const results = await Promise.allSettled(apiPromises);

  // Extract results with proper TypeScript handling
  const userCheckResult = results[0].status === 'fulfilled' ? results[0].value : null;
  let caseTicket: string | undefined;
  let leadTicket: string | undefined;

  let resultIndex = 1;
  if (caseSalesPromise) {
    const caseResult = results[resultIndex];
    caseTicket = caseResult.status === 'fulfilled' ? (caseResult as PromiseFulfilledResult<string | undefined>).value : undefined;
    resultIndex++;
  }
  if (leadPromise) {
    const leadResult = results[resultIndex];
    leadTicket = leadResult.status === 'fulfilled' ? (leadResult as PromiseFulfilledResult<string | undefined>).value : undefined;
  }

  // Handle user profile update (can be done in parallel with form submission)
  const ticket = caseTicket || leadTicket;
  if (userNumber) {
    // Fire and forget - don't wait for user profile update
    sendUserProfile(
      userNumber,
      form.email,
      form.formCount,
      form.phone,
      config.lastUpdatedBy,
      ticket,
    ).catch(error => console.error('Error updating user profile:', error));
  }

  // Handle any API failures
  results.forEach((result, index) => {
    if (result.status === 'rejected') {
      console.error(`API call ${index} failed:`, result.reason);
    }
  });

  return {
    userCheckResult,
    caseTicket,
    leadTicket,
  };
};

const ContactSales: React.FC<DynamicContactSalesProps> = ({
  data,
  // handleFormSubmit,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState<FormData>({
    email: '',
    name: '',
    formCount: '0',
    phone: '',
  });
  const [error, setError] = useState('');

  const config = getClientConfig();
  const dispatch = useDispatch<AppDispatch>();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (): Promise<void> => {
    setIsLoading(true);
    setError('');

    try {
      // Process all API calls in parallel
      const apiResults = await processApiCalls(form, config);
      console.log(apiResults)
      setIsSubmitted(true); 

      // Store email in localStorage
      localStorage.setItem(LOCAL_STORAGE_KEYS.userEmail, form.email);
      // const promptFormCount = Number(form.formCount);
      // let extraMessage = '';

      // if (promptFormCount < Number(formcount1)) {
      //   extraMessage =
      //     SALES_UI_TEXT.SALES_FLOW_BELOW500;
      // } else if (promptFormCount < Number(formcount2)) {
      //   extraMessage =
      //     SALES_UI_TEXT.SALES_FLOW_BELOW1000;
      // } else {
      //   extraMessage =
      //     SALES_UI_TEXT.SALES_FLOW_ABOVE1000;
      // }
      const roomId = localStorage.getItem('userNumber');
      const milestone_prompt = `Write a friendly, supportive, and affirming message that:
      A ticket has been created for your request, and our sales associate will reach out over this chat shortly.
      Keep a one line gap for: Ends by asking a polite question inviting them to either sign up and explore the platform if they’d like to or ask more queries or clarity on features.
      Keeps the tone warm, human, and helpful (not pushy or salesy)
      Limit to 2–3 sentences max
      Assume this message is shown after the user has submitted their email.`;
      
      socket.emit('milestone_trigger', milestone_prompt, roomId);

      dispatch({
        type: ADD_LOADER_EVENT,
        payload: {
          type: 'loader',
          id: `loader-${Date.now()}`,
          senderType: 'Chat bot',
        },
      });
      // Determine next action based on client type and user check result
      // const actionType = getNextActionType(
      //   clientType,
      //   apiResults.userCheckResult,
      //   data,
      // );

      // handleFormSubmit(actionType, { ...form, formCount: form.formCount });

    } catch (error: any) {
      const errorMessage = error?.message || 'An unexpected error occurred';
      setError(errorMessage);
      setIsSubmitted(false);
      console.error('Form submission error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = isValidEmail(form.email) && isValidPositiveNumber(form.formCount);

  return (
    <div className="p-2">
      <div className="mb-2 text-left">{data.title ?? 'I understand that you are looking for assistance; could you please provide your email so we can help you further?'}</div>

      <label htmlFor="email" className="block mb-2 font-semibold text-gray-600">
        {SALES_UI_TEXT.EMAIL_LABEL}
      </label>
      <input
        name="Email"
        type="email"
        id="Email"
        placeholder={SALES_UI_TEXT.EMAIL_PLACEHOLDER}
        className={`block py-2.5 px-0 mb-3 w-full text-sm text-gray-900 bg-transparent rounded-md pl-2 bg-[#F0F1F3] border-2 border-[#D7D7D7] appearance-none focus:outline-none focus:ring-0 ${clientType === 'salesbot2290ai'
            ? 'focus:border-secondary'
            : 'focus:border-[#6491C3]'
          } peer`}
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        required
      />
      <div>
        <label
          htmlFor="phone"
          className="block mb-2 font-semibold text-gray-600"
        >
          Phone Number
        </label>
        <PhoneInputField
          type="tel"
          id="phone"
          name="phone"
          placeholder="Enter Phone Number"
          isPhone={true}
          viewOnly={false}
          value={formatPhoneNumber(form.phone)}
          onChange={(e: any) => {
            const { value } = e.target;
            const filteredValue = value.replace(/[^0-9]/g, '');
            if (filteredValue.length <= 15) {
                    setForm({ ...form, phone: filteredValue });
                  }
          } }
          required
          maxLength={13} label={''} isSubmittedOnce={false}/>
      </div>

      <div className="flex flex-row gap-2">
        <div className="flex-1 relative z-0 w-full">
          <label
            htmlFor="numberOfForms"
            className="block mb-2 font-semibold text-gray-600"
          >
            {config.noOfFormsLabel}
          </label>
          <input
            name="Number of Forms"
            type="number"
            id="NumberOfForms"
            placeholder={config.placeholderValue}
            className={`block py-2.5 px-0 mb-3 w-full text-sm text-gray-900 bg-transparent rounded-md pl-2 bg-[#F0F1F3] border-2 border-[#D7D7D7] appearance-none focus:outline-none focus:ring-0 ${clientType === 'salesbot2290ai'
                ? 'focus:border-secondary'
                : 'focus:border-[#6491C3]'
              } peer`}
            value={form.formCount}
            onChange={(e) => setForm({ ...form, formCount: e.target.value })}
          />
        </div>

        <div className="flex-1 pt-7 relative w-full">
          <ButtonWithLoading
            isLoading={isLoading}
            type="submit"
            label={SALES_UI_TEXT.SUBMIT_BUTTON_LABEL}
            className={`${clientType === 'salesbot2290ai' ? 'bg-primary' : 'bg-[#114175]'
              } text-white p-3 rounded-lg w-full disabled:bg-gray-400 ${isFormValid
                ? `${clientType === 'salesbot2290ai'
                  ? 'hover:bg-secondary'
                  : 'hover:bg-[#6491C3]'
                } cursor-pointer`
                : 'cursor-not-allowed opacity-50'
              }`}
            disabled={!isFormValid || isSubmitted}
            onClick={handleSubmit}
          />
        </div>
      </div>

      {error && (
        <div className="mb-4 text-left text-red-500" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

export default ContactSales;